import { Component, computed, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { map } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash';
import { EnumValues } from '@card-software/library';
import { Roll, RollCategory } from '@app/models/knitting/roll';
import { Fabric } from '@app/models/order/work-order';
import { ProductionCardBundle, ProductionCardBundleStatus } from '@app/models/production/production-card/production-card-bundle';
import {
  FinalQcAction,
  FinalQcRollForm,
  PendingQcChange,
  RollsDisplayTableForm,
  RollsTableViewMode,
} from '@app/models/production/production-card/rolls-display-table';
import { EnumService } from '@app/services/enum.service';
import { LocaleHelperService } from '@app/services/locale-helper.service';
import { LocationPathService } from '@app/services/location-path.service';
import { KnittingUtil } from '@app/utilities/knitting-util';

@Component({
  selector: 'app-rolls-display-table',
  templateUrl: './rolls-display-table.component.html',
  styleUrl: './rolls-display-table.component.scss',
})
export class RollsDisplayTableComponent implements OnChanges {
  private readonly enumService = inject(EnumService);
  private readonly localeHelperService = inject(LocaleHelperService);
  private readonly translateService = inject(TranslateService);
  readonly locationPathService = inject(LocationPathService);

  @Input() isReadOnly = false;
  @Input() selectedBundle: ProductionCardBundle | null = null;
  @Input() rolls: Roll[] = [];
  @Input() fabrics: Fabric[] = [];
  @Input() viewMode: RollsTableViewMode = 'default';
  @Input() selectedRolls: Roll[] = [];
  @Input() selectionDisabled = false;
  @Input() disabledRollNumbers: string[] = [];
  @Input() disabledRollTooltip = '';
  @Input() useFinalWeight = false;
  @Input() showFinalWeightIndicator = false;
  @Output() removedRollFromBundle = new EventEmitter<string>();
  @Output() selectionChange = new EventEmitter<Roll[]>();
  @Output() finalQcAction = new EventEmitter<FinalQcAction>();

  rollsInOverrideMode: Set<string> = new Set();
  readonly finalQcForm = new FormGroup<RollsDisplayTableForm>({
    rolls: new FormArray<FormGroup<FinalQcRollForm>>([]),
  });
  private readonly finalQcFormGroupsByRollNumber = new Map<string, FormGroup<FinalQcRollForm>>();

  fabricFormValues: EnumValues = this.enumService.getEnum('FabricForm', 'itemEnums')?.values || {};
  rollQualityValues: EnumValues = this.enumService.getEnum('RollQuality', 'productionEnums')?.values || {};
  rollCategoryOptions: { label: string; value: number }[] = Object.entries(
    this.enumService.getEnum('RollCategory', 'productionEnums')?.values || {},
  ).map(([key, label]) => ({ label: label as string, value: Number(key) }));

  private readonly currentLang = toSignal(this.translateService.onLangChange.pipe(map((event) => event.lang)), {
    initialValue: this.translateService.currentLang,
  });

  readonly finalQcOptions = computed(() => {
    this.currentLang();
    return [
      { label: this.localeHelperService.translateWithFallback('Rolls_Display_Table_Approve', 'Approve'), value: true },
      { label: this.localeHelperService.translateWithFallback('Rolls_Display_Table_Reject', 'Reject'), value: false },
    ];
  });

  get finalQcRollsFormArray(): FormArray<FormGroup<FinalQcRollForm>> {
    return this.finalQcForm.controls.rolls;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rolls'] || changes['selectedBundle'] || changes['fabrics'] || changes['viewMode']) {
      this.syncFinalQcForm();
    }
    // The Location column is only rendered in the reprocess view.
    if (this.showReprocessColumns && (changes['rolls'] || changes['selectedBundle'] || changes['viewMode'])) {
      this.locationPathService.load(this.rollsToDisplay.map((roll) => roll.currentLocationCode));
    }
  }

  get rollsToDisplay(): Roll[] {
    return this.selectedBundle?.rolls ?? this.rolls;
  }

  get showRemoveAction(): boolean {
    return this.selectedBundle !== null && this.selectedBundle.status === ProductionCardBundleStatus.Processing;
  }

  get hasBundleActualWeight(): boolean {
    return this.selectedBundle !== null && (this.selectedBundle.actualWeightInLbs ?? 0) > 0;
  }

  get bundleActualWeightInLbs(): number {
    return this.selectedBundle?.actualWeightInLbs ?? 0;
  }

  get bundleActualWeightInKg(): number {
    const lbs = this.bundleActualWeightInLbs;
    if (lbs === 0) return 0;
    return lbs / 2.20462;
  }

  removeRollFromBundle(rollNumber: string): void {
    this.removedRollFromBundle.emit(rollNumber);
  }

  getRollStrict(item: unknown) {
    return item as Roll;
  }

  getRollWeightInKg(roll: Roll): number {
    const weightInLbs = this.getRollWeightInLbs(roll);
    if (weightInLbs === 0) {
      return 0;
    }
    return weightInLbs / 2.20462;
  }

  getFabric(fabricCode: string): Fabric | undefined {
    return this.fabrics.find((fab) => fab.fabricCode === fabricCode);
  }

  getTotalRollCount(): number {
    return this.rollsToDisplay.length;
  }

  getTotalWeightInLbs(): number {
    return this.rollsToDisplay.reduce((total, roll) => total + this.getRollWeightInLbs(roll), 0);
  }

  getRollWeightInLbs(roll: Roll): number {
    return KnittingUtil.getRollTimeStamp(roll)?.weightInLbs || 0;
  }

  getTotalWeightInKg(): number {
    const totalLbs = this.getTotalWeightInLbs();
    if (totalLbs === 0) {
      return 0;
    }
    return totalLbs / 2.20462;
  }

  getFinalWeightInLbs(roll: Roll): number {
    return roll.finalWeight?.weightInLbs || 0;
  }

  getFinalWeightInKg(roll: Roll): number {
    const weightInLbs = this.getFinalWeightInLbs(roll);
    if (weightInLbs === 0) {
      return 0;
    }
    return weightInLbs / 2.20462;
  }

  getLastLabelPrintDate(roll: Roll): Date | null {
    if (!roll.finalLabelPrints || roll.finalLabelPrints.length === 0) {
      return null;
    }
    const sortedPrints = [...roll.finalLabelPrints].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    return sortedPrints[0]?.date || null;
  }

  hasFinalWeight(roll: Roll): boolean {
    return this.getFinalWeightInLbs(roll) > 0;
  }

  hasRollWeight(roll: Roll): boolean {
    return this.getRollWeightInLbs(roll) > 0;
  }

  get showFinishedGoodsColumns(): boolean {
    return this.viewMode === 'finished-goods';
  }

  get showReprocessColumns(): boolean {
    return this.viewMode === 'reprocess';
  }

  get showFinalQcColumns(): boolean {
    return this.viewMode === 'final-qc';
  }

  get showSelectionColumn(): boolean {
    return this.viewMode === 'reprocess';
  }

  get showActions(): boolean {
    return this.showRemoveAction && !this.isReadOnly;
  }

  get showFinalQcActions(): boolean {
    return this.viewMode === 'final-qc' && !this.isReadOnly;
  }

  onFinalQcChange(roll: Roll, isApproved: boolean): void {
    const control = this.getFinalQcFormGroup(roll)?.controls.isApproved;
    if (!control) return;

    control.setValue(isApproved);
    control.markAsDirty();
  }

  getPendingChanges(): PendingQcChange[] {
    const changes: PendingQcChange[] = [];

    for (const roll of this.rollsToDisplay) {
      if (!roll.number) continue;
      if (this.isRollAssignedToProductionCard(roll)) continue;

      const formGroup = this.getFinalQcFormGroup(roll);
      if (!formGroup || !this.hasPendingChange(formGroup)) continue;

      const controls = formGroup.controls;
      const isApproved = controls.isApproved.dirty ? controls.isApproved.value! : (roll.finalQC?.isApproved ?? false);
      const finalGsm = controls.finalGsm.dirty ? controls.finalGsm.value : roll.finalQC?.finalGsm;
      const finalWidth = controls.finalWidth.dirty ? controls.finalWidth.value : roll.finalQC?.finalWidth;
      const finalLengthInYards = controls.finalLengthInYards.dirty ? controls.finalLengthInYards.value : roll.finalLengthInYards;
      const rollCategory = controls.rollCategory.dirty ? controls.rollCategory.value : (roll.rollCategory ?? null);
      const hold = controls.hold.dirty ? controls.hold.value : roll.finalQC?.hold;

      changes.push({ roll, isApproved, finalGsm, finalWidth, finalLengthInYards, rollCategory, hold });
    }

    return changes;
  }

  getPendingChangeCount(): number {
    return this.rollsToDisplay.filter((roll) => {
      if (!roll.number || this.isRollAssignedToProductionCard(roll)) return false;
      const formGroup = this.getFinalQcFormGroup(roll);
      return formGroup ? this.hasPendingChange(formGroup) : false;
    }).length;
  }

  hasPendingQcApproval(roll: Roll): boolean {
    return this.getFinalQcFormGroup(roll)?.controls.isApproved.dirty ?? false;
  }

  getPendingApprovalValue(roll: Roll): boolean | undefined {
    return this.getFinalQcFormGroup(roll)?.controls.isApproved.value ?? undefined;
  }

  clearPendingApproval(roll: Roll): void {
    const control = this.getFinalQcFormGroup(roll)?.controls.isApproved;
    if (!control) return;

    control.reset(null, { emitEvent: false });
  }

  private isRollEditable(roll: Roll): boolean {
    if (!roll.number) return false;
    if (this.isRollAssignedToProductionCard(roll)) return false;
    return !this.hasFinalQcStatus(roll) || this.rollsInOverrideMode.has(roll.number);
  }

  approveAllPendingRolls(): void {
    for (const roll of this.rollsToDisplay) {
      if (this.isRollEditable(roll)) {
        this.onFinalQcChange(roll, true);
      }
    }
  }

  rejectAllPendingRolls(): void {
    for (const roll of this.rollsToDisplay) {
      if (this.isRollEditable(roll)) {
        this.onFinalQcChange(roll, false);
      }
    }
  }

  setGsmForAllPendingRolls(gsm: number): void {
    for (const roll of this.rollsToDisplay) {
      if (this.isRollEditable(roll)) {
        this.setFinalGsmValue(roll, gsm);
      }
    }
  }

  setWidthForAllPendingRolls(width: number): void {
    for (const roll of this.rollsToDisplay) {
      if (this.isRollEditable(roll)) {
        this.setFinalWidthValue(roll, width);
      }
    }
  }

  clearPendingChanges(): void {
    this.rollsInOverrideMode.clear();
    this.finalQcRollsFormArray.controls.forEach((formGroup) => this.resetFinalQcFormGroup(formGroup));
  }

  clearPendingChangeForRoll(rollNumber: string): void {
    this.rollsInOverrideMode.delete(rollNumber);
    const formGroup = this.finalQcRollsFormArray.controls.find((candidate) => candidate.controls.roll.value.number === rollNumber);
    if (formGroup) {
      this.resetFinalQcFormGroup(formGroup);
    }
  }

  getEditableRollCount(): number {
    return this.rollsToDisplay.filter((roll) => this.isRollEditable(roll)).length;
  }

  getFinalGsmValue(roll: Roll): number | undefined {
    const formGroup = this.getFinalQcFormGroup(roll);
    return formGroup ? formGroup.controls.finalGsm.value : this.getInitialFinalGsmValue(roll);
  }

  setFinalGsmValue(roll: Roll, value: number | undefined): void {
    this.setPendingControlValue(this.getFinalQcFormGroup(roll)?.controls.finalGsm, value);
  }

  private getInitialFinalGsmValue(roll: Roll): number | undefined {
    if (!_.isNil(roll.finalQC?.finalGsm)) {
      return roll.finalQC.finalGsm;
    }
    if (this.hasFinalQcStatus(roll)) {
      const fabric = this.getFabric(roll.expectedFabricCode!);
      return fabric?.gramsPerSquareMeter;
    }
    return undefined;
  }

  getFinalWidthValue(roll: Roll): number | undefined {
    const formGroup = this.getFinalQcFormGroup(roll);
    return formGroup ? formGroup.controls.finalWidth.value : this.getInitialFinalWidthValue(roll);
  }

  setFinalWidthValue(roll: Roll, value: number | undefined): void {
    this.setPendingControlValue(this.getFinalQcFormGroup(roll)?.controls.finalWidth, value);
  }

  private getInitialFinalWidthValue(roll: Roll): number | undefined {
    if (!_.isNil(roll.finalQC?.finalWidth)) {
      return roll.finalQC.finalWidth;
    }
    if (this.hasFinalQcStatus(roll)) {
      const fabric = this.getFabric(roll.expectedFabricCode!);
      return fabric?.endToEndWidth;
    }
    return undefined;
  }

  getFinalLengthInYardsValue(roll: Roll): number | undefined {
    const formGroup = this.getFinalQcFormGroup(roll);
    return formGroup ? formGroup.controls.finalLengthInYards.value : roll.finalLengthInYards;
  }

  setFinalLengthInYardsValue(roll: Roll, value: number | undefined): void {
    this.setPendingControlValue(this.getFinalQcFormGroup(roll)?.controls.finalLengthInYards, value);
  }

  isRollAssignedToProductionCard(roll: Roll): boolean {
    return !_.isNil(roll.productionCardReference);
  }

  hasFinalQcStatus(roll: Roll): boolean {
    return !_.isNil(roll.finalQC);
  }

  getFinalQcStatusLabel(roll: Roll): string {
    if (!roll.finalQC) return '-';

    if (roll.finalQC.isApproved === undefined || roll.finalQC.isApproved === null) {
      return '-';
    }
    return roll.finalQC.isApproved
      ? this.localeHelperService.translateWithFallback('Rolls_Display_Table_Approved', 'Approved')
      : this.localeHelperService.translateWithFallback('Rolls_Display_Table_Rejected', 'Rejected');
  }

  isRollInOverrideMode(roll: Roll): boolean {
    return roll.number ? this.rollsInOverrideMode.has(roll.number) : false;
  }

  enableOverrideMode(roll: Roll): void {
    if (roll.number) {
      this.rollsInOverrideMode.add(roll.number);
      const formGroup = this.getFinalQcFormGroup(roll);
      if (!formGroup || !roll.finalQC) return;

      this.updateFinalQcFormGroupDisabledState(formGroup);
      this.setPendingControlValue(formGroup.controls.finalGsm, roll.finalQC.finalGsm);
      this.setPendingControlValue(formGroup.controls.finalWidth, roll.finalQC.finalWidth);
      this.setPendingControlValue(formGroup.controls.finalLengthInYards, roll.finalLengthInYards);
      this.setPendingControlValue(formGroup.controls.rollCategory, roll.rollCategory ?? null);
      this.setPendingControlValue(formGroup.controls.hold, roll.finalQC.hold ?? false);
    }
  }

  getCategoryValue(roll: Roll): number | null {
    const formGroup = this.getFinalQcFormGroup(roll);
    return formGroup ? formGroup.controls.rollCategory.value : (roll.rollCategory ?? null);
  }

  setCategoryValue(roll: Roll, value: number | null | undefined): void {
    this.setPendingControlValue(this.getFinalQcFormGroup(roll)?.controls.rollCategory, value ?? null);
  }

  getCategoryOptionsForRoll(roll: Roll): { label: string; value: number }[] {
    // In override mode with no pending approval change yet, show all options so the user
    // can correct both the approval and category freely before saving.
    if (this.isRollInOverrideMode(roll) && !this.hasPendingQcApproval(roll)) {
      return this.rollCategoryOptions;
    }

    // Get the approval status (pending or existing)
    const isApproved = this.hasPendingQcApproval(roll) ? this.getPendingApprovalValue(roll) : roll.finalQC?.isApproved;

    if (isApproved === true) {
      // First Quality: only FirstQuality and LeftOverAssignable
      return this.rollCategoryOptions.filter(
        (opt) => opt.value === RollCategory.FirstQuality || opt.value === RollCategory.LeftOverAssignable,
      );
    } else if (isApproved === false) {
      // BGrade: only BGrade, LeftOverNotAssignable, and LargeBGrade
      return this.rollCategoryOptions.filter(
        (opt) =>
          opt.value === RollCategory.BGrade || opt.value === RollCategory.LeftOverNotAssignable || opt.value === RollCategory.LargeBGrade,
      );
    }
    // If no approval status yet, show all options
    return this.rollCategoryOptions;
  }

  getOnHoldValue(roll: Roll): boolean {
    return this.getFinalQcFormGroup(roll)?.controls.hold.value ?? roll.finalQC?.hold ?? false;
  }

  setOnHoldValue(roll: Roll, value: boolean): void {
    this.setPendingControlValue(this.getFinalQcFormGroup(roll)?.controls.hold, value);
  }

  cancelOverrideMode(roll: Roll): void {
    if (roll.number) {
      this.rollsInOverrideMode.delete(roll.number);
      const formGroup = this.getFinalQcFormGroup(roll);
      if (formGroup) {
        this.resetFinalQcFormGroup(formGroup);
      }
    }
  }

  getFinalQcFormIndex(roll: Roll): number {
    return this.finalQcRollsFormArray.controls.findIndex((formGroup) => formGroup === this.getFinalQcFormGroup(roll));
  }

  private syncFinalQcForm(): void {
    if (!this.showFinalQcColumns) return;

    this.rollsToDisplay.forEach((roll) => {
      const existingFormGroup = this.getFinalQcFormGroup(roll);
      if (!existingFormGroup) {
        const formGroup = this.createFinalQcFormGroup(roll);
        this.finalQcRollsFormArray.push(formGroup);
        if (roll.number) {
          this.finalQcFormGroupsByRollNumber.set(roll.number, formGroup);
        }
        return;
      }

      existingFormGroup.controls.roll.setValue(roll, { emitEvent: false });
      this.refreshPristineFinalQcValues(existingFormGroup);
      this.updateFinalQcFormGroupDisabledState(existingFormGroup);
    });
  }

  private createFinalQcFormGroup(roll: Roll): FormGroup<FinalQcRollForm> {
    const formGroup = new FormGroup<FinalQcRollForm>({
      roll: new FormControl<Roll>(roll, { nonNullable: true }),
      finalLengthInYards: new FormControl<number | undefined>(roll.finalLengthInYards, { nonNullable: true }),
      finalGsm: new FormControl<number | undefined>(this.getInitialFinalGsmValue(roll), { nonNullable: true }),
      finalWidth: new FormControl<number | undefined>(this.getInitialFinalWidthValue(roll), { nonNullable: true }),
      rollCategory: new FormControl<number | null>(roll.rollCategory ?? null),
      hold: new FormControl<boolean>(roll.finalQC?.hold ?? false, { nonNullable: true }),
      isApproved: new FormControl<boolean | null>(null),
    });

    this.updateFinalQcFormGroupDisabledState(formGroup);
    return formGroup;
  }

  private getFinalQcFormGroup(roll: Roll): FormGroup<FinalQcRollForm> | undefined {
    if (roll.number) {
      return this.finalQcFormGroupsByRollNumber.get(roll.number);
    }

    return this.finalQcRollsFormArray.controls.find((formGroup) => {
      const formRoll = formGroup.controls.roll.value;
      return formRoll === roll;
    });
  }

  private refreshPristineFinalQcValues(formGroup: FormGroup<FinalQcRollForm>): void {
    const roll = formGroup.controls.roll.value;
    const controls = formGroup.controls;

    if (controls.finalLengthInYards.pristine) controls.finalLengthInYards.setValue(roll.finalLengthInYards, { emitEvent: false });
    if (controls.finalGsm.pristine) controls.finalGsm.setValue(this.getInitialFinalGsmValue(roll), { emitEvent: false });
    if (controls.finalWidth.pristine) controls.finalWidth.setValue(this.getInitialFinalWidthValue(roll), { emitEvent: false });
    if (controls.rollCategory.pristine) controls.rollCategory.setValue(roll.rollCategory ?? null, { emitEvent: false });
    if (controls.hold.pristine) controls.hold.setValue(roll.finalQC?.hold ?? false, { emitEvent: false });
    if (controls.isApproved.pristine) controls.isApproved.setValue(null, { emitEvent: false });
  }

  private resetFinalQcFormGroup(formGroup: FormGroup<FinalQcRollForm>): void {
    const controls = formGroup.controls;
    const roll = controls.roll.value;

    controls.finalLengthInYards.reset(roll.finalLengthInYards, { emitEvent: false });
    controls.finalGsm.reset(this.getInitialFinalGsmValue(roll), { emitEvent: false });
    controls.finalWidth.reset(this.getInitialFinalWidthValue(roll), { emitEvent: false });
    controls.rollCategory.reset(roll.rollCategory ?? null, { emitEvent: false });
    controls.hold.reset(roll.finalQC?.hold ?? false, { emitEvent: false });
    controls.isApproved.reset(null, { emitEvent: false });
    this.updateFinalQcFormGroupDisabledState(formGroup);
  }

  private updateFinalQcFormGroupDisabledState(formGroup: FormGroup<FinalQcRollForm>): void {
    const roll = formGroup.controls.roll.value;
    const disabled = this.isRollAssignedToProductionCard(roll) || (this.hasFinalQcStatus(roll) && !this.isRollInOverrideMode(roll));
    const editableControls = [
      formGroup.controls.finalLengthInYards,
      formGroup.controls.finalGsm,
      formGroup.controls.finalWidth,
      formGroup.controls.rollCategory,
      formGroup.controls.hold,
      formGroup.controls.isApproved,
    ];

    editableControls.forEach((control) => {
      if (disabled) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    });
  }

  private hasPendingChange(formGroup: FormGroup<FinalQcRollForm>): boolean {
    const controls = formGroup.controls;
    return (
      controls.isApproved.dirty ||
      controls.rollCategory.dirty ||
      controls.hold.dirty ||
      controls.finalGsm.dirty ||
      controls.finalWidth.dirty ||
      controls.finalLengthInYards.dirty
    );
  }

  private setPendingControlValue<T>(control: FormControl<T> | undefined, value: T): void {
    if (!control) return;

    control.setValue(value);
    control.markAsDirty();
  }

  getDisplayWeightInLbs(roll: Roll): number {
    // Final Weight if registered, else the most recent stamp of any type (Production, QualityControl,
    // ParentSplit, ChildSplit) — not just the restricted QC/Production classification, so a roll that
    // was split (or otherwise adjusted) after its last QC stamp still shows its actual current weight.
    // See Bug 5097.
    return KnittingUtil.getCurrentWeightInLbs(roll);
  }

  getDisplayWeightInKg(roll: Roll): number {
    const weightInLbs = this.getDisplayWeightInLbs(roll);
    if (weightInLbs === 0) {
      return 0;
    }
    return weightInLbs / 2.20462;
  }

  getDisplayWeightInYards(roll: Roll): number {
    // Reprocess mode shows the roll's own recorded length instead of an estimate from poundToYard
    if (this.viewMode === 'reprocess') {
      return roll.finalLengthInYards ?? 0;
    }
    const weightInLbs = this.getDisplayWeightInLbs(roll);
    const fabric = this.getFabric(roll.expectedFabricCode!);
    if (weightInLbs === 0 || !fabric?.poundToYard) {
      return 0;
    }
    return weightInLbs * fabric.poundToYard;
  }

  getTotalDisplayWeightInLbs(): number {
    return this.rollsToDisplay.reduce((total, roll) => total + this.getDisplayWeightInLbs(roll), 0);
  }

  getTotalDisplayWeightInKg(): number {
    const totalLbs = this.getTotalDisplayWeightInLbs();
    if (totalLbs === 0) {
      return 0;
    }
    return totalLbs / 2.20462;
  }

  getTotalDisplayWeightInYards(): number {
    return this.rollsToDisplay.reduce((total, roll) => total + this.getDisplayWeightInYards(roll), 0);
  }

  onSelectionChange(selection: Roll[]): void {
    this.selectionChange.emit(selection);
  }

  getRollCategoryLabel(roll: Roll): string {
    if (roll.rollCategory === null || roll.rollCategory === undefined) {
      return '-';
    }
    return this.rollCategoryOptions.find((option) => option.value === roll.rollCategory)?.label ?? '-';
  }

  /**
   * Bound to the table's `rowSelectable`, which PrimeNG honours in "select all" and in the header
   * checkbox's own checked state — a per-row `[disabled]` alone does neither. Kept as a field so the
   * reference stays stable across change detection.
   */
  isRowSelectable = (event: { data: Roll; index: number }): boolean => !this.isRollExcluded(event.data);

  /** Excluded by the parent for a per-row reason, as opposed to the table-wide `selectionDisabled`. */
  isRollExcluded(roll: Roll): boolean {
    return !!roll?.number && this.disabledRollNumbers.includes(roll.number);
  }

  isRollSelectionDisabled(roll: Roll): boolean {
    return this.selectionDisabled || this.isRollExcluded(roll);
  }

  getSelectedRollCount(): number {
    return this.selectedRolls.length;
  }

  // First Weight (reprocess mode): the roll's own weight, independent of Final QC
  getSelectedTotalFirstWeightInLbs(): number {
    return this.selectedRolls.reduce((total, roll) => total + this.getRollWeightInLbs(roll), 0);
  }

  getSelectedTotalFirstWeightInKg(): number {
    const totalLbs = this.getSelectedTotalFirstWeightInLbs();
    if (totalLbs === 0) {
      return 0;
    }
    return totalLbs / 2.20462;
  }

  getSelectedTotalFinalWeightInLbs(): number {
    return this.selectedRolls.reduce((total, roll) => total + this.getFinalWeightInLbs(roll), 0);
  }

  getSelectedTotalFinalWeightInKg(): number {
    const totalLbs = this.getSelectedTotalFinalWeightInLbs();
    if (totalLbs === 0) {
      return 0;
    }
    return totalLbs / 2.20462;
  }

  getTotalRollCountFromRolls(): number {
    return this.rollsToDisplay.length;
  }

  getTotalWeightInLbsFromRolls(): number {
    return this.rollsToDisplay.reduce((total, roll) => total + this.getRollWeightInLbs(roll), 0);
  }

  getTotalWeightInKgFromRolls(): number {
    const totalLbs = this.getTotalWeightInLbsFromRolls();
    if (totalLbs === 0) {
      return 0;
    }
    return totalLbs / 2.20462;
  }

  getTotalFinalWeightInLbs(): number {
    return this.rollsToDisplay.reduce((total, roll) => total + this.getFinalWeightInLbs(roll), 0);
  }

  getTotalFinalWeightInKg(): number {
    const totalLbs = this.getTotalFinalWeightInLbs();
    if (totalLbs === 0) {
      return 0;
    }
    return totalLbs / 2.20462;
  }

  getRollWeightInYards(roll: Roll): number {
    const weightInLbs = this.getRollWeightInLbs(roll);
    const fabric = this.getFabric(roll.expectedFabricCode!);
    if (weightInLbs === 0 || !fabric?.poundToYard) {
      return 0;
    }
    return weightInLbs * fabric.poundToYard;
  }

  getFinalWeightInYards(roll: Roll): number {
    const weightInLbs = this.getFinalWeightInLbs(roll);
    const fabric = this.getFabric(roll.expectedFabricCode!);
    if (weightInLbs === 0 || !fabric?.poundToYard) {
      return 0;
    }
    return weightInLbs * fabric.poundToYard;
  }

  getTotalWeightInYards(): number {
    if (this.viewMode === 'reprocess') {
      return this.rollsToDisplay.reduce((total, roll) => total + (roll.finalLengthInYards ?? 0), 0);
    }
    return this.rollsToDisplay.reduce((total, roll) => total + this.getRollWeightInYards(roll), 0);
  }

  getTotalFinalWeightInYards(): number {
    return this.rollsToDisplay.reduce((total, roll) => total + this.getFinalWeightInYards(roll), 0);
  }

  getSelectedTotalWeightInYards(): number {
    return this.selectedRolls.reduce((total, roll) => total + this.getDisplayWeightInYards(roll), 0);
  }
}
